import React, { useState, useEffect } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Square,
  Fence,
  DoorOpen,
  AppWindow,
  Ruler,
  Home,
  Flower2,
  Sprout,
  Circle,
  Droplets,
  Recycle,
  LayoutGrid,
  MousePointer2,
  Hand,
} from 'lucide-react';
import wegIcon from '../../assets/icons/weg.png';
import hochbeetIcon from '../../assets/icons/hochbeet_sprout.png';
import gardenhouseIcon from '../../assets/icons/gardenhouse.png';
import {
  getAllCategories,
  getObjectTypesByCategory,
} from '../../config';
import { useHasCoarsePointer } from '../../hooks/useHasCoarsePointer';

/**
 * Icon mapping for garden object types
 * Supports both Lucide React icons and custom PNG icons
 */
const ICON_MAP = {
  // Lucide icons
  Square,
  Fence,
  DoorOpen,
  AppWindow,
  Ruler,
  Home,
  Flower2,
  Sprout,
  Circle,
  Droplets,
  Recycle,
  LayoutGrid,
  // Custom PNG icons
  wegIcon,
  hochbeetIcon,
  gardenhouseIcon,
};

/**
 * GardenObjectSidebar - Sidebar for selecting garden object types
 *
 * Features:
 * - Select tool button at top
 * - Displays all 16 garden object types organized by 5 categories
 * - Collapsible category groups
 * - Object buttons with icons and names
 * - Highlights currently selected object type and tool
 * - Callback when object type or tool is selected
 *
 * @param {string} selectedObjectType - Currently selected object type ID
 * @param {function} onSelectObjectType - Callback(typeId) when object type is selected
 * @param {string} activeTool - Currently selected tool
 * @param {function} onToolChange - Callback(toolId) when tool is selected
 */
const GardenObjectSidebar = ({ selectedObjectType, onSelectObjectType, activeTool, onToolChange }) => {
  const isTouchDevice = useHasCoarsePointer();

  // Track which categories are expanded (all collapsed by default, persisted to localStorage)
  const [expandedCategories, setExpandedCategories] = useState(() => {
    // Try to load saved state from localStorage
    try {
      const saved = localStorage.getItem('gartenplaner_sidebar_expanded');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.warn('Failed to load sidebar state from localStorage:', error);
    }

    // Default: all categories collapsed
    const categories = getAllCategories();
    return categories.reduce((acc, cat) => {
      acc[cat.id] = false;
      return acc;
    }, {});
  });

  // Save expanded state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('gartenplaner_sidebar_expanded', JSON.stringify(expandedCategories));
    } catch (error) {
      console.warn('Failed to save sidebar state to localStorage:', error);
    }
  }, [expandedCategories]);

  /**
   * Toggle category expansion
   */
  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  /**
   * Handle object type selection
   */
  const handleSelectObjectType = (typeId) => {
    onSelectObjectType?.(typeId);
  };

  /**
   * Get icon component for object type
   */
  const getIconComponent = (iconName) => {
    return ICON_MAP[iconName] || Square;
  };

  const categories = getAllCategories();

  return (
    <div className="h-full flex flex-col bg-gray-50 border-r border-gray-200">
      {/* Tool cluster: Wählen + (auf Touch) Bewegen — gemeinsamer Block ohne Divider dazwischen */}
      <div className="px-4 py-3 border-b border-gray-200 bg-white space-y-2">
        <button
          onClick={() => onToolChange?.('select')}
          className={`
            w-full px-3 py-2 rounded flex items-center justify-center gap-2
            transition-all duration-150
            ${
              activeTool === 'select'
                ? 'bg-emerald-500 text-white shadow-sm'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }
          `}
          title="Auswählen (V)"
        >
          <MousePointer2 size={18} />
          <span className="text-sm font-medium">Wählen</span>
        </button>

        {isTouchDevice && (
          <button
            onClick={() => onToolChange?.('pan')}
            className={`
              w-full px-3 py-2 rounded flex items-center justify-center gap-2
              transition-all duration-150
              ${
                activeTool === 'pan'
                  ? 'bg-emerald-500 text-white shadow-sm'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }
            `}
            title="Bewegen"
          >
            <Hand size={18} />
            <span className="text-sm font-medium">Bewegen</span>
          </button>
        )}
      </div>

      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-white">
        <h3 className="text-base font-semibold text-gray-900">Gartenobjekte</h3>
        <p className="text-xs text-gray-500 mt-1">Wähle ein Objekt zum Zeichnen</p>
      </div>

      {/* Categories and Objects */}
      <div className="flex-1 overflow-y-auto">
        {categories.map((category) => {
          const objectTypes = getObjectTypesByCategory(category.id);
          const isExpanded = expandedCategories[category.id];

          return (
            <div key={category.id} className="border-b border-gray-200">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category.id)}
                className="w-full px-4 py-3 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown size={16} className="text-gray-500" />
                  ) : (
                    <ChevronRight size={16} className="text-gray-500" />
                  )}
                  <span className="text-sm font-medium text-gray-900">
                    {category.name}
                  </span>
                  <span className="text-xs text-gray-500">
                    ({objectTypes.length})
                  </span>
                </div>
              </button>

              {/* Object Type Buttons */}
              {isExpanded && (
                <div className="bg-gray-50 px-2 py-2 space-y-1">
                  {objectTypes.map((objectType) => {
                    const iconNameOrComponent = getIconComponent(objectType.icon);
                    const isSelected = selectedObjectType === objectType.id;
                    const isPngIcon = typeof iconNameOrComponent === 'string';

                    return (
                      <button
                        key={objectType.id}
                        onClick={() => handleSelectObjectType(objectType.id)}
                        className={`
                          w-full px-3 py-2 rounded flex items-center gap-3
                          transition-all duration-150
                          ${
                            isSelected
                              ? 'bg-emerald-500 text-white shadow-sm'
                              : 'bg-white text-gray-700 hover:bg-gray-100'
                          }
                        `}
                        title={objectType.name}
                      >
                        {isPngIcon ? (
                          <img
                            src={iconNameOrComponent}
                            alt={objectType.name}
                            className="w-[18px] h-[18px]"
                          />
                        ) : (
                          React.createElement(iconNameOrComponent, {
                            size: 18,
                            className: isSelected ? 'text-white' : 'text-gray-500',
                          })
                        )}
                        <span className="text-sm font-medium">{objectType.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer - Instructions */}
      <div className="px-4 py-3 border-t border-gray-200 bg-white">
        <p className="text-xs text-gray-500">
          Nach der Auswahl eines Objekts wähle ein Werkzeug zum Zeichnen.
        </p>
      </div>
    </div>
  );
};

export default GardenObjectSidebar;
